import type { ConversationState } from "@ai-admin/shared";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router";
import { trpc } from "../../app/trpc.js";
import { Avatar, Badge, ChatIcon, EmptyState, focusRing, Notice, PageTitle, SkeletonList, useWide } from "../../ui/index.js";
import { fmtAgo, useCabinet } from "../cabinet/index.js";
import { ChatPage } from "./ChatPage.js";
import { stateTone } from "./state.js";

type Filter = ConversationState | "all";
const FILTERS: Filter[] = ["escalated", "staff", "bot", "paused", "all"];

/**
 * Диалоги Instagram по состояниям (демо-чат здесь не показываем). Обновляется раз в 15 секунд.
 * На телефоне список, открытый диалог (`/conversations/:id`) занимает экран целиком.
 * На широком экране список слева, переписка справа; без выбранного диалога справа подсказка.
 */
export function ConversationsPage() {
  const { t } = useTranslation();
  const { conversationId } = useParams();
  const wide = useWide();
  // Переписка всегда на одной позиции в дереве: при пересечении 1024px (поворот планшета) она не перемонтируется,
  // черновик ответа и открытая шторка остаются. Высота на широком экране: экран минус отступы main и плашка админа.
  return (
    <div className="lg:grid lg:h-[calc(100dvh-4rem-var(--shell-banner,0px))] lg:grid-cols-[20rem_minmax(0,1fr)] lg:gap-6">
      {(wide || !conversationId) && <ConversationList selectedId={conversationId ?? null} />}
      {conversationId ? (
        <section className="flex min-h-0 flex-col">
          <ChatPage key={conversationId} />
        </section>
      ) : (
        wide && (
          <section className="flex min-h-0 flex-col">
            <div className="flex flex-1 items-center justify-center rounded-card border border-dashed border-line-strong">
              <EmptyState icon={<ChatIcon />} title={t("conv.selectHint")} />
            </div>
          </section>
        )
      )}
    </div>
  );
}

function ConversationList({ selectedId }: { selectedId: string | null }) {
  const { t, i18n } = useTranslation();
  const cab = useCabinet();
  const [filter, setFilter] = useState<Filter>("all");
  const list = useQuery(
    trpc.inbox.list.queryOptions(
      { salonId: cab.salonId, channel: "instagram", limit: 100, ...(filter !== "all" ? { state: filter } : {}) },
      { refetchInterval: 15_000 },
    ),
  );
  const now = new Date();
  return (
    <div className="flex min-h-0 flex-col">
      <PageTitle>{t("conv.title")}</PageTitle>
      {/* Переключатель в одну строку: пункты по ширине подписи, лишнее место делится поровну. Пять коротких подписей
          умещаются и в 390px, и в колонке списка на широком экране; если язык длиннее, лента листается без полосы прокрутки. */}
      <fieldset aria-label={t("conv.title")} className="no-scrollbar mb-4 flex min-w-0 gap-1 overflow-x-auto rounded-field bg-white p-1 ring-1 ring-line">
        {FILTERS.map((f) => (
          <button
            key={f}
            type="button"
            aria-pressed={filter === f}
            onClick={() => setFilter(f)}
            className={`flex-auto whitespace-nowrap rounded-[9px] px-2 py-1.5 text-[13px] font-medium transition ${focusRing} ${
              filter === f ? "bg-brand text-white" : "text-ink-2 hover:bg-slate-50 hover:text-ink"
            }`}
          >
            {t(`conv.filter.${f}`)}
          </button>
        ))}
      </fieldset>
      {list.isPending && <SkeletonList rows={3} />}
      {list.isError && <Notice kind="error">{t("common.error")}</Notice>}
      {list.data?.length === 0 && <EmptyState icon={<ChatIcon />} title={filter === "escalated" ? t("conv.noneWaiting") : t("conv.empty")} />}
      <div className="space-y-3 lg:min-h-0 lg:flex-1 lg:overflow-y-auto lg:pr-1">
        {list.data?.map((c) => (
          <Link
            key={c.id}
            to={`${cab.base}/conversations/${c.id}`}
            aria-current={c.id === selectedId ? "page" : undefined}
            className={`flex gap-3 rounded-card border px-4 py-3 shadow-card transition ${focusRing} ${
              c.id === selectedId
                ? "border-brand bg-brand-soft"
                : c.state === "escalated"
                  ? "border-amber-300 bg-white hover:border-amber-400"
                  : "border-line bg-white hover:border-line-strong"
            }`}
          >
            <Avatar name={c.client_name ?? "?"} />
            <div className="min-w-0 flex-1">
              <div className="flex items-center justify-between gap-2">
                <div className="truncate font-semibold text-ink">{c.client_name ?? t("conv.instagramClient")}</div>
                <span className="shrink-0 text-xs text-ink-3">{fmtAgo(new Date(c.updated_at), now, cab.timezone, i18n.language)}</span>
              </div>
              {c.last_text && (
                <div className="mt-0.5 truncate text-sm text-ink-2">
                  {c.last_author && c.last_author !== "client" && <span className="text-ink-3">{t(`conv.author.${c.last_author}`)}: </span>}
                  {c.last_text}
                </div>
              )}
              <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1">
                <Badge tone={stateTone[c.state as ConversationState]} dot>
                  {t(`conv.state.${c.state}`)}
                </Badge>
                {c.state === "escalated" && c.escalation_reason && (
                  <span className="text-xs text-amber-700">{t(`conv.reason.${c.escalation_reason}`, c.escalation_reason)}</span>
                )}
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
